<template>
  <div
    class="d-sticky"
    :class="{'with-margin' : !noMargin}"
  >
    <v-skeleton-loader
      v-if="loading"
      type="list-item-avatar-two-line"
      class="mb-2"
    />
    <v-banner
      v-if="!loading"
      single-line
      sticky
      color="party_header"
      class="mb-2"
    >
      <template v-if="create">
        <span>
          <strong>
            New Party
          </strong>
        </span>
      </template>

      <template v-else>
        <v-row
          align="center"
        >
          <v-col
            cols="auto"
            class="my-1"
          >
            <v-hover v-slot="{ hover }">
              <template v-if="peUserCanUpdate">
                <v-badge
                  bordered
                  color="secondary"
                  icon="mdi-pencil"
                  overlap
                  :offset-x="16"
                  :offset-y="16"
                >
                  <v-avatar
                    v-if="!hover"
                    size="56"
                    color="blue-grey darken-5"
                  >
                    <img
                      v-if="hasPhoto"
                      :src="peGetPhotoDataUrl"
                    >
                    <v-progress-circular
                      v-else-if="pePhotoLoading"
                      indeterminate
                      color="white"
                      size="24"
                    />
                    <span
                      v-else
                      class="white--text text-caption font-weight-bold"
                    >No<br>Photo</span>
                  </v-avatar>
                  <v-avatar
                    v-else
                    size="56"
                    color="blue-grey darken-5"
                    class="pointer"
                  >
                    <v-btn
                      icon
                      @click="showPhotoUploadDialog = true"
                    >
                      <v-icon color="white">
                        mdi-camera-plus
                      </v-icon>
                    </v-btn>
                  </v-avatar>
                </v-badge>
              </template>
              <template v-else>
                <v-avatar
                  size="56"
                  color="blue-grey darken-5"
                >
                  <img
                    v-if="hasPhoto"
                    :src="peGetPhotoDataUrl"
                  >
                  <v-progress-circular
                    v-else-if="pePhotoLoading"
                    indeterminate
                    color="white"
                    size="24"
                  />
                  <span
                    v-else
                    class="white--text text-caption font-weight-bold"
                  >No<br>Photo</span>
                </v-avatar>
              </template>
            </v-hover>
          </v-col>
          <v-col>
            <span>
              <strong class="text-uppercase">
                {{ partyFullName }}
              </strong>

              <div
                class="text-caption d-block"
              >
                {{ peParty.gender_string }} | DOB:
                {{ peParty.dob ? dateDisplayFormat : '-' }}
                <strong
                  v-if="peParty.dob || peParty.uncertain_dob"
                >
                  ({{ calculatedAge }})
                </strong>
                {{
                  aNumberFormatDisplay
                }}
              </div>
            </span>
            <span>
              <v-chip
                v-for="tag of peParty.tags"
                :key="tag.id"
                class="ma-2"
                close
                small
                :color="tag.color"
                text-color="white"
                @click:close="onUnTagParty(tag)"
              >
                {{ tag.name }}
              </v-chip>
            </span>
          </v-col>
          <DocketwiseButtons />
          <EcoMemberPass />
          <QuickInterviewButton />
          <KioskActions />
          <v-col cols="auto">
            <v-tooltip bottom>
              <template
                #activator="{on, attrs}"
              >
                <v-btn
                  v-bind="attrs"
                  icon
                  v-on="on"
                  @click="onCollapseToggle"
                >
                  <v-icon>
                    {{ collapsableIcon }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ collapsableText }}</span>
            </v-tooltip>

            <v-tooltip
              bottom
            >
              <template
                #activator="{ on, attrs }"
              >
                <v-btn
                  v-bind="attrs"
                  icon
                  v-on="on"
                  @click="showRecords"
                >
                  <v-icon>
                    plagiarism
                  </v-icon>
                </v-btn>
              </template>

              <span>Associated Records</span>
            </v-tooltip>

            <v-tooltip
              bottom
            >
              <template
                #activator="{ on, attrs }"
              >
                <v-btn
                  v-bind="attrs"
                  icon
                  :color="adverseIndividualsIconColor"
                  v-on="on"
                  @click="showAdverseIndividuals"
                >
                  <v-icon>
                    warning_amber
                  </v-icon>
                </v-btn>
              </template>
              <span>View/Add Adverse Individuals</span>
            </v-tooltip>

            <v-tooltip
              v-if="peUserCanUpdate"
              bottom
            >
              <template
                #activator="{ on, attrs }"
              >
                <v-btn
                  v-bind="attrs"
                  icon
                  v-on="on"
                  @click="onShowTagParty"
                >
                  <v-icon>
                    mdi-tag-outline
                  </v-icon>
                </v-btn>
              </template>
              <span>Tag Parties</span>
            </v-tooltip>

            <v-menu>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>
                    mdi-dots-vertical
                  </v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  @click="teamsDialog = true"
                >
                  <v-list-item-icon
                    class="mr-4"
                  >
                    <v-icon>
                      mdi-account-group
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Teams with Access</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
      </template>
    </v-banner>
    <!-- Records dialog -->
    <RecordsDialog
      :display-dialog="showRecordsDialog"
      :party-name="partyFullName"
      :records="recordsList"
      @close-dialog="showRecordsDialog = false"
    />
    <!-- Adverse Individuals Dialog -->
    <AdverseIndividualDialog
      :adverse-individual-data="adverseIndividualsList"
      :adverse-to="peParty.id"
      :view-only="false"
    />
    <PEPartyPhotoDialog
      v-model="showPhotoUploadDialog"
    />
    <TagPartyDialog />
    <ResourceTeamsDialog
      v-model="teamsDialog"
      :teams="peParty.teams"
      :resource-id="peParty.id"
      resource-type="parties"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { aNumberFormatDisplay, calculatedAge, dateDisplayFormat } from '@/utilities'
import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'
import { SubscribeToThisPartyChangesChannel } from '@/store/party/channel'
import { USER_NAMESPACE } from '@/store/user'

import AdverseIndividualDialog from '@/components/Dialogs/AdverseIndividual.vue'
import DocketwiseButtons from '@/components/Party/PartyEdit/HeaderTools/DocketwiseButtons.vue'
import EcoMemberPass from '@/components/Party/PartyEdit/HeaderTools/EcoMemberPass.vue'
import KioskActions from '@/components/Party/PartyEdit/HeaderTools/KioskActions.vue'
import PEPartyPhotoDialog from '@/components/Party/PartyEdit/SectionTools/PEPartyPhotoDialog.vue'
import QuickInterviewButton from '@/components/Party/PartyEdit/HeaderTools/QuickInterviewButton.vue'
import RecordsDialog from '@/components/Dialogs/Records.vue'
import ResourceTeamsDialog from '@/components/Team/Dialogs/ResourceTeamsDialog.vue'
import TagPartyDialog from '@/components/Dialogs/Party/TagParty.vue'

export default {
  name: 'PEHeader',

  components: {
    AdverseIndividualDialog,
    DocketwiseButtons,
    EcoMemberPass,
    KioskActions,
    PEPartyPhotoDialog,
    QuickInterviewButton,
    RecordsDialog,
    ResourceTeamsDialog,
    TagPartyDialog,
  },

  props: {
    create: {
      type: Boolean,
      default: false,
    },

    noMargin: {
      type: Boolean,
      default: false,
    },

    quickEdit: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      collapsed: false,
      partySubscription: null,
      showPhotoUploadDialog: false,
      showRecordsDialog: false,
      teamsDialog: false,
    }
  },

  computed: {
    aNumberFormatDisplay () {
      return this.peParty.a_number ? `| A# ${aNumberFormatDisplay(this.peParty.a_number)}` : ''
    },

    adverseIndividualsIconColor () { return this.adverseIndividualsList.length > 0 ? 'red' : '' },
    adverseIndividualsList () { return this.peParty?.adverse_individuals || [] },
    calculatedAge () { return calculatedAge(this.peParty.dob, this.peParty.uncertain_dob) },
    collapsableIcon () { return this.collapsed ? 'unfold_more' : 'unfold_less' },
    collapsableText () { return this.collapsed ? 'Expand All' : 'Collapse All' },
    dateDisplayFormat () { return dateDisplayFormat(this.peParty.dob) },

    hasPhoto () {
      return !!this.peGetPhotoDataUrl
    },

    loading () {
      if (this.create) return false
      return this.pePartyLoading
    },

    pageTitle () {
      // The quick-edit dialog renders PEHeader on top of whatever page opened it
      // (e.g. a record view) without navigating away from it, so it must not
      // touch document.title - only the dedicated party-edit page should.
      if (this.quickEdit || !this.peParty?.id) return ''
      const sectionName = this.$route.meta?.sectionName
      return `${this.partyFullName}${sectionName ? ` - ${sectionName}` : ''} - Innovation Law Lab Platform`
    },

    partyFullName () {
      const headerString = [this.peParty.first_name]
      if (this.peParty.middle_name) headerString.push(this.peParty.middle_name)
      headerString.push(this.peParty.last_name)
      headerString.push(`(ID: ${this.peParty.id})`)

      return headerString.join(' ')
    },

    recordsList () { return this.peParty?.records || [] },

    ...mapGetters(PARTY_NAMESPACE, [
      'peGetPhotoDataUrl',
      'peParty',
      'pePartyLoading',
      'pePhotoLoading',
      'peUserCanUpdate',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'currentUser',
    ]),
  },

  watch: {
    pageTitle: {
      immediate: true,

      handler (val) {
        if (val) document.title = val
      },
    },

    'peParty.id': {

      handler (newVal) {
        if (newVal && !this.partySubscription) {
          this.partySubscription = SubscribeToThisPartyChangesChannel(newVal, data => {
            if (data.params.user === this.currentUser.id) return

            if (data.action === 'UPDATED_PARTY') {
              this.pePartyOtherUpdated({ value: true })

              this.displayAlert('info',
                'This Party was updated by another user .',
                null,
                null
              )
            } else if (data.action === 'UPDATED_CHARACTERISTIC') {
              this.pePartySetOthersUpdated({ identifier: data.params.identifier, value: true })

              this.displayAlert('info',
                `A characteristic <strong>${data.params.association_name}</strong> was updated in this Party by another user.`,
                null,
                null
              )
            } else if (data.action === 'CREATED_CHARACTERISTIC') {
              this.pePartyCharacteristicOthersCreated({ characteristic: data.params.association_name, value: data.params.characteristic })

              this.displayAlert('info',
                `A characteristic <strong>${data.params.association_name}</strong> was created in this Party by another user.`,
                null,
                null
              )
            } else if (data.action === 'DELETED_CHARACTERISTIC') {
              this.pePartyCharacteristicOthersDeleted({ characteristic: data.params.association_name, id: data.params.characteristic_id })

              this.displayAlert('info',
                `A characteristic <strong>${data.params.association_name}</strong> was deleted in this Party by another user.`,
                null,
                null
              )
            }
          })
        }
      },
    },
  },

  destroyed () {
    if (this.partySubscription) {
      this.partySubscription.unsubscribe()
      this.partySubscription = null
    }

    this.peResetPartyEdit()
  },

  methods: {
    onCollapseToggle () {
      this.collapsed = !this.collapsed
      this.peSetCollapsedAllCards(this.collapsed)
    },

    onShowTagParty () {
      this.setShowTagPartyDialog(true)
    },

    onUnTagParty (tag) {
      this.untagParty({ id: this.peParty.id, tagId: tag.id })
    },

    showAdverseIndividuals () { this.setViewAdverseIndividualsDialog(true) },
    showRecords () { this.showRecordsDialog = true },

    ...mapActions(PARTY_NAMESPACE, [
      'pePartyCharacteristicOthersCreated',
      'pePartyCharacteristicOthersDeleted',
      'pePartyOtherUpdated',
      'pePartySetOthersUpdated',
      'peResetPartyEdit',
      'peSetCollapsedAllCards',
      'untagParty',
    ]),

    ...mapMutations(PARTY_NAMESPACE, [
      'setShowTagPartyDialog',
    ]),

    ...mapMutations([
      'setViewAdverseIndividualsDialog',
    ]),
  },
}
</script>

<style scoped lang="scss">
.d-sticky {
  position: sticky !important;
  z-index: 1;
  top: 0px;
  &.with-margin {
    top: 72px;
  }
}
</style>
